'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonVariant,
    Justify,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';
import { ProductParentFull } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';
import { ResponsiveSpacer } from '@components/responsive-spacer';

import { TradeInStep } from '@constants/trade-in';

import TradeInIcon from '@assets/logo-trade-in.svg';
import { getTradeInStep } from '@product-detail/helpers';

export type ProductOffersDiscountsProps = {
    product: ProductParentFull;
    tradeInStep: TradeInStep;
    tradeInBonusPlan?: string;
    openTradeInModal: () => void;
    tradeInBonus: number;
    resetTradeIn: () => void;
    isRenewal: boolean;
    plan?: MobilePlanType | null;
};

export function ProductOffersDiscounts({
    product,
    tradeInStep,
    tradeInBonusPlan,
    openTradeInModal,
    tradeInBonus,
    resetTradeIn,
    isRenewal,
    plan,
}: Readonly<ProductOffersDiscountsProps>) {
    const [tradeInPhone, setTradeInPhone] = useState<string>('');
    const [tradeInPrice, setTradeInPrice] = useState<number>(0);

    useEffect(() => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            const product = getTradeInStep(TradeInStep.SEARCH);
            const tradeIn = getTradeInStep(TradeInStep.CONFIRMATION);
            if (tradeInPhone !== product?.name) {
                setTradeInPhone(product?.name.replace(/ /g, '\u00A0') ?? '');
                setTradeInPrice(tradeIn?.amount ?? 0);
            }
        }
    }, [tradeInStep]);

    const renderContent = () => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            return (
                <>
                    <Text level={TextLevels.TWO}>
                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Voici le montant estimé{' '}
                        </Text>
                        <Text markup={TextMarkup.SPAN}>
                            pour l&apos;achat de ce nouveau téléphone
                            {tradeInBonus > 0 ? ` avec forfait Bouygues Telecom ${tradeInBonusPlan}` : ''},{' '}
                        </Text>
                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            si vous vendez votre {tradeInPhone}.
                        </Text>
                    </Text>
                    <Box flat shadowless>
                        {tradeInBonus > 0 ? (
                            <>
                                <BoxContent className='p-small'>
                                    <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER}>
                                        <div>
                                            <Text level={TextLevels.TWO} marginless>
                                                Valeur estimée du
                                            </Text>
                                            <Text level={TextLevels.TWO}>{tradeInPhone}&nbsp;:</Text>
                                        </div>
                                        <InlinePrice level={TextLevels.TWO} amount={tradeInPrice} />
                                    </View>
                                </BoxContent>
                                <BoxContent className='p-small'>
                                    <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER}>
                                        <Text level={TextLevels.TWO} marginless>
                                            Bonus reprise&nbsp;:
                                        </Text>
                                        <InlinePrice level={TextLevels.TWO} amount={tradeInBonus} />
                                    </View>
                                </BoxContent>
                                <BoxContent backgroundColor={TrilogyColor.MAIN_FADE} className='p-small'>
                                    <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER}>
                                        <Text level={TextLevels.TWO} marginless>
                                            Montant total estimé&nbsp;:
                                        </Text>
                                        <InlinePrice
                                            amount={tradeInPrice + tradeInBonus}
                                            className='title is-level-5'
                                        />
                                    </View>
                                </BoxContent>
                            </>
                        ) : (
                            <BoxContent backgroundColor={TrilogyColor.MAIN_FADE} className='p-small'>
                                <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER}>
                                    <div>
                                        <Text level={TextLevels.TWO} marginless>
                                            Valeur estimée du
                                        </Text>
                                        <Text level={TextLevels.TWO}>{tradeInPhone}&nbsp;:</Text>
                                    </div>
                                    <InlinePrice amount={tradeInPrice} className='title is-level-5' />
                                </View>
                            </BoxContent>
                        )}
                    </Box>
                </>
            );
        } else {
            return (
                <>
                    <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                        Estimez-le au meilleur prix et bénéficiez d&apos;un bonus reprise.
                    </Text>
                    {tradeInBonus > 0 ? (
                        <Text level={TextLevels.TWO}>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                <span>Jusqu&apos;à&nbsp;</span>
                                <InlinePrice amount={tradeInBonus} level={TextLevels.TWO} />
                                <span>&nbsp;de bonus reprise&nbsp;</span>
                            </Text>
                            <Text markup={TextMarkup.SPAN}>
                                pour l&apos;achat de ce téléphone et la souscription d&apos;un forfait Bouygues Telecom{' '}
                                {tradeInBonusPlan} si vous vendez votre ancien mobile.
                            </Text>
                        </Text>
                    ) : (
                        <Text level={TextLevels.TWO}>
                            Profitez d&apos;une remise différée sur votre nouveau téléphone en faisant reprendre votre
                            ancien mobile. Vous y gagnez, et l&apos;environnement aussi.
                        </Text>
                    )}
                </>
            );
        }
    };

    const renderActionButtons = () => {
        switch (tradeInStep) {
            case TradeInStep.NOT_STARTED:
            case TradeInStep.SEARCH:
                return (
                    <Button variant={ButtonVariant.PRIMARY} onClick={openTradeInModal}>
                        Estimer mon mobile
                    </Button>
                );
            case TradeInStep.CONFIRMED:
                return (
                    <ButtonList>
                        <Button variant={ButtonVariant.SECONDARY} onClick={openTradeInModal}>
                            Modifier ma reprise
                        </Button>
                        <Button variant={ButtonVariant.GHOST} onClick={resetTradeIn}>
                            Annuler ma reprise
                        </Button>
                    </ButtonList>
                );
            default:
                return (
                    <Button variant={ButtonVariant.PRIMARY} onClick={openTradeInModal}>
                        Finaliser mon estimation
                    </Button>
                );
        }
    };

    if (isRenewal && plan === undefined) {
        return null;
    }

    return (
        <Box shadowless>
            <BoxContent>
                <View flexable align={Align.CENTER}>
                    <Image src={TradeInIcon} alt='' width={40} />
                    <Spacer size={SpacerSize.THREE} horizontal />
                    <Title level={TitleLevels.FOUR}>Vous avez un mobile à revendre&nbsp;?</Title>
                </View>
                <ResponsiveSpacer mobileSize={SpacerSize.FOUR} desktopSize={SpacerSize.FIVE} />
                <form id='offersDiscounts'>
                    {renderContent()}
                    {renderActionButtons()}
                    <Cms content={product.specialOffersCms} />
                </form>
            </BoxContent>
        </Box>
    );
}
